import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FileText,
  FolderSearch,
  LayoutDashboard,
  ListTodo,
  Loader2,
  Mic,
  Search,
  ShieldCheck,
  User,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PluginMetricCards } from '@/app/components/PluginMetricCards';
import { PluginPageHeader } from '@/app/components/PluginPageHeader';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import { useApiToken } from '@/auth/hooks';
import { useClarifyStore } from './useClarifyStore';
import { NewRequestDialog } from './NewRequestDialog';
import {
  ClarifyDetailStatusBadge,
  ClarifyMainStatusBadge,
  ClarifyPriorityBadge,
} from './ClarifyStatusBadge';
import { toast } from 'sonner';
import { useInterviewStore } from '@/app/features/interviewer/useInterviewStore';
import { DocumentViewerDialog } from '@/app/features/talk-to-data/components/DocumentViewerDialog';
import { normalizeBusinessContextBody, parseBusinessContextSections } from './businessContextFormatting';
import type {
  ClarifyPriority,
  CreateClarifyRequestInput,
  ClarifyRequestDetail,
  ClarifyRequestSummary,
  ClarifyWorkspaceMember,
  RejectClarifyRequestInput,
  ReviewClarifyRequestInput,
  TriageClarifyRequestInput,
} from '@/types/clarify';

type ClarifyTab = 'overview' | 'requests' | 'tasks' | 'triage' | 'review';

const PRIORITY_OPTIONS: Array<{ value: ClarifyPriority; label: string }> = [
  { value: 'critical', label: 'Kritisch' },
  { value: 'high', label: 'Hoch' },
  { value: 'medium', label: 'Mittel' },
  { value: 'low', label: 'Niedrig' },
];

function roleRank(role?: string | null) {
  switch (role) {
    case 'Admin': return 3;
    case 'Reviewer': return 2;
    case 'Editor': return 1;
    default: return 0;
  }
}

function BusinessContextCard({ businessContext }: { businessContext: string }) {
  const sections = parseBusinessContextSections(businessContext);
  const fallbackText = normalizeBusinessContextBody(businessContext);

  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-5">
        <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Business-Kontext</h3>
        {sections.length > 0 ? (
          <div className="space-y-3">
            {sections.map((section) => (
              <div key={section.label} className="grid gap-1.5 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{section.label}</p>
                <p className="text-sm leading-6 text-foreground/90 whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm leading-6 text-muted-foreground whitespace-pre-line">{fallbackText}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function ClarifyPage() {
  const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);
  const currentUser = useOrgMemberStore((state) => state.currentUser);
  const loadCurrentUser = useOrgMemberStore((state) => state.loadCurrentUser);
  const { getAccessToken } = useApiToken();
  const navigate = useNavigate();

  const dashboard = useClarifyStore((state) => state.dashboard);
  const myRequests = useClarifyStore((state) => state.myRequests);
  const myTasks = useClarifyStore((state) => state.myTasks);
  const triageQueue = useClarifyStore((state) => state.triageQueue);
  const reviewQueue = useClarifyStore((state) => state.reviewQueue);
  const assignableMembers = useClarifyStore((state) => state.assignableMembers);
  const selectedRequest = useClarifyStore((state) => state.selectedRequest);
  const loading = useClarifyStore((state) => state.loading);
  const selectedRequestLoading = useClarifyStore((state) => state.selectedRequestLoading);
  const error = useClarifyStore((state) => state.error);
  const loadAll = useClarifyStore((state) => state.loadAll);
  const loadDetail = useClarifyStore((state) => state.loadDetail);
  const createRequest = useClarifyStore((state) => state.createRequest);
  const triageRequest = useClarifyStore((state) => state.triageRequest);
  const rejectRequest = useClarifyStore((state) => state.rejectRequest);
  const linkInterviewSession = useClarifyStore((state) => state.linkInterviewSession);
  const reviewRequest = useClarifyStore((state) => state.reviewRequest);
  const clearSelection = useClarifyStore((state) => state.clearSelection);

  const startSession = useInterviewStore((state) => state.startSession);
  const loadSession = useInterviewStore((state) => state.loadSession);
  const setActiveSessionId = useInterviewStore((state) => state.setActiveSessionId);

  const [activeTab, setActiveTab] = useState<ClarifyTab>('overview');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<{ id: string; fileName: string } | null>(null);

  const canSeeReview = roleRank(currentUser?.role) >= 2;
  const canSeeTriage = roleRank(currentUser?.role) >= 3;

  useEffect(() => {
    if (!activeWorkspace) return;

    let cancelled = false;
    const hydrate = async () => {
      const token = await getAccessToken().catch(() => null);
      if (!token || cancelled) return;
      if (!currentUser) {
        await loadCurrentUser(token);
      }
      await loadAll(activeWorkspace.id, token);
    };

    void hydrate();
    return () => { cancelled = true; };
  }, [activeWorkspace?.id, currentUser, getAccessToken, loadAll, loadCurrentUser]);

  useEffect(() => {
    if (!activeWorkspace || !selectedRequestId) {
      return;
    }

    let cancelled = false;
    const hydrateDetail = async () => {
      const token = await getAccessToken().catch(() => null);
      if (!token || cancelled) return;
      await loadDetail(activeWorkspace.id, selectedRequestId, token);
    };

    void hydrateDetail();
    return () => { cancelled = true; };
  }, [activeWorkspace?.id, getAccessToken, loadDetail, selectedRequestId]);

  const handleRefresh = async () => {
    if (!activeWorkspace) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;
    await loadAll(activeWorkspace.id, token);
    if (selectedRequestId) {
      await loadDetail(activeWorkspace.id, selectedRequestId, token);
    }
  };

  const handleCreateRequest = async (input: Parameters<typeof createRequest>[1]) => {
    if (!activeWorkspace) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;
    await createRequest(activeWorkspace.id, input, token);
    setActiveTab('requests');
    toast.success('Knowledge Request erstellt');
  };

  const handleTriageRequest = async (requestId: string, input: TriageClarifyRequestInput) => {
    if (!activeWorkspace) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;
    await triageRequest(activeWorkspace.id, requestId, input, token);
    toast.success('Request triagiert und zugewiesen');
  };

  const handleTriageAndStartInterview = async (
    request: ClarifyRequestSummary,
    input: TriageClarifyRequestInput,
  ) => {
    if (!activeWorkspace) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;

    await triageRequest(activeWorkspace.id, request.id, input, token);

    const sessionId = await startSession(
      activeWorkspace.id,
      input.assigneeUserId,
      request.title,
      request.question,
      undefined,
      token,
    );

    await linkInterviewSession(activeWorkspace.id, request.id, sessionId, token);
    navigate('/app/interviewer');
    toast.success('Request zugewiesen und Sopho Interviewer geöffnet');
  };

  const handleStartInterview = async (request: ClarifyRequestSummary) => {
    if (!activeWorkspace || !currentUser) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;

    let sessionId = request.interviewSessionId ?? undefined;
    if (sessionId) {
      await loadSession(sessionId, token);
      setActiveSessionId(sessionId);
    } else {
      sessionId = await startSession(
        activeWorkspace.id,
        currentUser.userId,
        request.title,
        request.question,
        undefined,
        token,
      );
      await linkInterviewSession(activeWorkspace.id, request.id, sessionId, token);
    }

    navigate('/app/interviewer');
    toast.success('Sopho Interviewer geöffnet');
  };

  const handleRejectRequest = async (requestId: string, input: RejectClarifyRequestInput) => {
    if (!activeWorkspace) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;
    await rejectRequest(activeWorkspace.id, requestId, input, token);
    toast.success('Request wurde abgelehnt');
  };

  const handleReview = async (requestId: string, input: ReviewClarifyRequestInput) => {
    if (!activeWorkspace) return;
    const token = await getAccessToken().catch(() => null);
    if (!token) return;
    await reviewRequest(activeWorkspace.id, requestId, input, token);
    toast.success(input.action === 'approve' ? 'Request freigegeben' : input.action === 'revision' ? 'Nacharbeit angefordert' : 'Request abgelehnt');
  };

  const selectRequest = (requestId: string) => {
    setSelectedRequestId(requestId);
  };

  const handleBack = () => {
    setSelectedRequestId(null);
    clearSelection();
  };

  const handleOpenLinkedDocument = (documentId: string) => {
    navigate(`/app/documents?documentId=${encodeURIComponent(documentId)}`);
  };

  const handlePreviewLinkedDocument = (documentId: string, fileName: string) => {
    setPreviewDocument({ id: documentId, fileName });
  };

  if (!activeWorkspace) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
        Bitte zuerst einen Workspace auswählen.
      </div>
    );
  }

  if (selectedRequestId) {
    return (
      <div className="flex h-full w-full min-w-0 flex-col px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
        {selectedRequestLoading && !selectedRequest ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Request…</div>
        ) : selectedRequest ? (
          <RequestDetailView
            request={selectedRequest}
            onBack={handleBack}
            onStartInterview={handleStartInterview}
            onOpenLinkedDocument={handleOpenLinkedDocument}
            onPreviewLinkedDocument={handlePreviewLinkedDocument}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">Request konnte nicht geladen werden.</div>
        )}

        <DocumentViewerDialog
          documentId={previewDocument?.id ?? null}
          fileName={previewDocument?.fileName}
          onClose={() => setPreviewDocument(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
      <PluginPageHeader
        group="analysis"
        title="Sopho Clarify"
        icon={FileText}
        actions={<Button variant="outline" onClick={handleRefresh}>Aktualisieren</Button>}
      />

      {error ? (
        <Card className="border-destructive/40">
          <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : null}

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ClarifyTab)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="overflow-x-auto border-b border-border">
          <TabsList className="h-11 bg-transparent p-0">
            <TabsTrigger value="overview" className="gap-2"><LayoutDashboard className="h-4 w-4" />Übersicht</TabsTrigger>
            <TabsTrigger value="requests" className="gap-2"><FileText className="h-4 w-4" />Meine Requests</TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2"><ListTodo className="h-4 w-4" />Meine Tasks</TabsTrigger>
            {canSeeTriage ? <TabsTrigger value="triage" className="gap-2"><ShieldCheck className="h-4 w-4" />Triage</TabsTrigger> : null}
            {canSeeReview ? <TabsTrigger value="review" className="gap-2"><ClipboardCheck className="h-4 w-4" />Review</TabsTrigger> : null}
          </TabsList>
        </div>

        <TabsContent value="overview" className="min-h-0 flex-1 overflow-auto">
          <DashboardView dashboard={dashboard} loading={loading} onSelectRequest={selectRequest} onNavigate={setActiveTab} />
        </TabsContent>
        <TabsContent value="requests" className="min-h-0 flex-1 overflow-auto">
          <MyRequestsView requests={myRequests.items} loading={loading} onSelectRequest={selectRequest} onCreateRequest={handleCreateRequest} />
        </TabsContent>
        <TabsContent value="tasks" className="min-h-0 flex-1 overflow-auto">
          <MyTasksView requests={myTasks.items} loading={loading} onSelectRequest={selectRequest} onStartInterview={handleStartInterview} />
        </TabsContent>
        {canSeeTriage ? (
          <TabsContent value="triage" className="min-h-0 flex-1 overflow-auto">
            <TriageView requests={triageQueue.items} members={assignableMembers} loading={loading} onSelectRequest={selectRequest} onTriage={handleTriageRequest} onReject={handleRejectRequest} onTriageAndStartInterview={handleTriageAndStartInterview} />
          </TabsContent>
        ) : null}
        {canSeeReview ? (
          <TabsContent value="review" className="min-h-0 flex-1 overflow-auto">
            <ReviewView requests={reviewQueue.items} loading={loading} onSelectRequest={selectRequest} onReview={handleReview} onOpenSteward={() => navigate('/app/steward')} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

function DashboardView({
  dashboard,
  loading,
  onSelectRequest,
  onNavigate,
}: {
  dashboard: ReturnType<typeof useClarifyStore.getState>['dashboard'];
  loading: boolean;
  onSelectRequest: (requestId: string) => void;
  onNavigate: (tab: ClarifyTab) => void;
}) {
  const cards = [
    { id: 'request', label: 'Request', value: dashboard.requestCount, tab: 'triage' as const, icon: FolderSearch, tone: 'neutral' as const },
    { id: 'in-progress', label: 'In Progress', value: dashboard.inProgressCount, tab: 'tasks' as const, icon: ListTodo, tone: 'warning' as const },
    { id: 'in-review', label: 'In Review', value: dashboard.inReviewCount, tab: 'review' as const, icon: ClipboardCheck, tone: 'info' as const },
    { id: 'solved', label: 'Solved', value: dashboard.solvedCount, tab: 'requests' as const, icon: CheckCircle2, tone: 'success' as const },
  ];

  return (
    <div className="space-y-6 py-4">
      <PluginMetricCards
        items={cards.map((card) => ({
          id: card.id,
          label: card.label,
          value: card.value,
          icon: card.icon,
          tone: card.tone,
          onClick: () => onNavigate(card.tab),
        }))}
        loading={loading}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <SummaryListCard title="Risiken & Blocker" icon={<AlertTriangle className="h-4 w-4 text-rose-600" />} items={dashboard.overdue.length > 0 ? dashboard.overdue : dashboard.urgent} emptyText="Keine kritischen Requests" onSelectRequest={onSelectRequest} />
        <SummaryListCard title="Anstehende Fristen" icon={<Calendar className="h-4 w-4 text-sky-600" />} items={dashboard.dueSoon} emptyText="Keine Fristen in den nächsten 7 Tagen" onSelectRequest={onSelectRequest} />
        <SummaryListCard title="Letzte Aktivität" icon={<Clock className="h-4 w-4 text-slate-500" />} items={dashboard.recentActivity} emptyText="Noch keine Aktivität" onSelectRequest={onSelectRequest} />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" className="gap-2" onClick={() => onNavigate('triage')}>Neue Requests triagieren <ArrowRight className="h-4 w-4" /></Button>
        <Button variant="outline" className="gap-2" onClick={() => onNavigate('review')}>Review-Queue öffnen <ArrowRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function SummaryListCard({
  title,
  icon,
  items,
  emptyText,
  onSelectRequest,
}: {
  title: string;
  icon: React.ReactNode;
  items: ClarifyRequestSummary[];
  emptyText: string;
  onSelectRequest: (requestId: string) => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold">{icon}{title}</div>
        {items.length === 0 ? <p className="text-xs text-muted-foreground">{emptyText}</p> : (
          <div className="space-y-2">
            {items.map((item) => (
              <button key={item.id} type="button" onClick={() => onSelectRequest(item.id)} className="w-full rounded-lg border border-border/60 p-3 text-left transition-colors hover:bg-secondary/60">
                <div className="mb-1 flex items-center gap-2">
                  <ClarifyMainStatusBadge status={item.mainStatus} />
                  <ClarifyPriorityBadge priority={item.priority} />
                </div>
                <p className="truncate text-sm font-medium">{item.title}</p>
                <p className="text-xs text-muted-foreground">{item.requestNumber}</p>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MyRequestsView({
  requests,
  loading,
  onSelectRequest,
  onCreateRequest,
}: {
  requests: ClarifyRequestSummary[];
  loading: boolean;
  onSelectRequest: (requestId: string) => void;
  onCreateRequest: (input: CreateClarifyRequestInput) => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return requests;
    return requests.filter((request) => request.title.toLowerCase().includes(query) || request.requestNumber.toLowerCase().includes(query));
  }, [requests, search]);

  return (
    <div className="space-y-4 py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Request suchen" />
        </div>
        <NewRequestDialog onSubmit={onCreateRequest} />
      </div>

      <RequestList requests={filtered} loading={loading} emptyText="Keine Requests gefunden" onSelectRequest={onSelectRequest} />
    </div>
  );
}

function MyTasksView({
  requests,
  loading,
  onSelectRequest,
  onStartInterview,
}: {
  requests: ClarifyRequestSummary[];
  loading: boolean;
  onSelectRequest: (requestId: string) => void;
  onStartInterview: (request: ClarifyRequestSummary) => Promise<void>;
}) {
  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Meine Interview Tasks</h2>
        <Badge variant="outline">{requests.length} zugewiesen</Badge>
      </div>
      {loading ? <LoaderRow /> : null}
      <div className="space-y-3">
        {requests.length === 0 && !loading ? <EmptyState text="Keine offenen Tasks zugewiesen" /> : null}
        {requests.map((request) => {
          const canStart = request.detailStatus === 'assigned' || request.detailStatus === 'accepted' || request.detailStatus === 'revision_requested';
          const canContinue = request.detailStatus === 'interview_in_progress' || request.detailStatus === 'awaiting_input';
          return (
            <Card key={request.id} className="transition-shadow hover:shadow-md">
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{request.requestNumber}</span>
                  <ClarifyMainStatusBadge status={request.mainStatus} />
                  <ClarifyDetailStatusBadge status={request.detailStatus} />
                  <ClarifyPriorityBadge priority={request.priority} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">{request.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{request.question}</p>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <span>Anfrage von: {request.requester.displayName}</span>
                  {request.dueDate ? <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(request.dueDate).toLocaleDateString('de-DE')}</span> : null}
                  {request.interviewTaskNumber ? <span className="flex items-center gap-1"><Mic className="h-3 w-3" />{request.interviewTaskNumber}</span> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="gap-2" onClick={() => void onStartInterview(request)}>{canContinue ? <ArrowRight className="h-4 w-4" /> : <Mic className="h-4 w-4" />}{canContinue ? 'Interview fortsetzen' : canStart ? 'Interview starten' : 'Interviewer öffnen'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => onSelectRequest(request.id)}>Details</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function TriageView({
  requests,
  members,
  loading,
  onSelectRequest,
  onTriage,
  onReject,
  onTriageAndStartInterview,
}: {
  requests: ClarifyRequestSummary[];
  members: ClarifyWorkspaceMember[];
  loading: boolean;
  onSelectRequest: (requestId: string) => void;
  onTriage: (requestId: string, input: TriageClarifyRequestInput) => Promise<void>;
  onReject: (requestId: string, input: RejectClarifyRequestInput) => Promise<void>;
  onTriageAndStartInterview: (request: ClarifyRequestSummary, input: TriageClarifyRequestInput) => Promise<void>;
}) {
  const [priorityFilter, setPriorityFilter] = useState<'all' | ClarifyPriority>('all');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<ClarifyPriority | ''>('');
  const [adminNote, setAdminNote] = useState('');

  const filtered = useMemo(() => {
    return requests.filter((request) => priorityFilter === 'all' || request.priority === priorityFilter);
  }, [priorityFilter, requests]);

  const selectedRequest = filtered.find((request) => request.id === selectedRequestId) ?? null;

  useEffect(() => {
    if (!selectedRequest) return;
    setPriority(selectedRequest.priority);
    setDueDate(selectedRequest.dueDate ? selectedRequest.dueDate.slice(0, 10) : '');
    setAdminNote(selectedRequest.adminNote ?? '');
  }, [selectedRequest]);

  return (
    <div className="grid min-h-0 gap-6 py-4 lg:grid-cols-[380px_minmax(0,1fr)]">
      <Card className="min-h-0">
        <CardContent className="flex h-full flex-col gap-3 p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-sky-600" />
            <h2 className="text-sm font-semibold">Triage-Queue</h2>
            <Badge variant="outline" className="ml-auto">{filtered.length} offen</Badge>
          </div>
          <Select value={priorityFilter} onValueChange={(value) => setPriorityFilter(value as 'all' | ClarifyPriority)}>
            <SelectTrigger>
              <SelectValue placeholder="Priorität filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Prioritäten</SelectItem>
              {PRIORITY_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="max-h-[40vh] min-h-0 flex-1 space-y-2 overflow-auto lg:max-h-none">
            {filtered.length === 0 && !loading ? <EmptyState text="Keine Requests zur Triage" compact /> : null}
            {filtered.map((request) => (
              <button key={request.id} type="button" onClick={() => setSelectedRequestId(request.id)} className={`w-full rounded-lg border p-3 text-left transition-colors ${selectedRequestId === request.id ? 'border-primary bg-secondary/70' : 'border-border/70 hover:bg-secondary/40'}`}>
                <div className="mb-1 flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{request.requestNumber}</span>
                  <ClarifyPriorityBadge priority={request.priority} />
                  <ClarifyDetailStatusBadge status={request.detailStatus} />
                </div>
                <p className="text-sm font-semibold">{request.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">von {request.requester.displayName}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="min-h-0">
        <CardContent className="flex h-full flex-col gap-4 p-5">
          {!selectedRequest ? <EmptyState text="Request aus der Liste auswählen" /> : (
            <>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{selectedRequest.requestNumber}</span>
                  <ClarifyMainStatusBadge status={selectedRequest.mainStatus} />
                  <ClarifyPriorityBadge priority={selectedRequest.priority} />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">{selectedRequest.title}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{selectedRequest.question}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => onSelectRequest(selectedRequest.id)}>Details</Button>
                </div>
              </div>

              {selectedRequest.businessContext ? (
                <BusinessContextCard businessContext={selectedRequest.businessContext} />
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Experte zuweisen</label>
                  <Select value={assigneeUserId} onValueChange={setAssigneeUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Mitglied auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {members.map((member) => (
                        <SelectItem key={member.userId} value={member.userId}>{member.displayName} · {member.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Fälligkeitsdatum</label>
                  <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Priorität</label>
                <Select value={priority || selectedRequest.priority} onValueChange={(value) => setPriority(value as ClarifyPriority)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Admin-Notiz</label>
                <Textarea value={adminNote} onChange={(event) => setAdminNote(event.target.value)} className="min-h-[96px]" />
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Button className="w-full sm:w-auto" disabled={!assigneeUserId} onClick={() => void onTriage(selectedRequest.id, { assigneeUserId, dueDate: dueDate || undefined, priority: priority || selectedRequest.priority, adminNote })}>Interview Task erstellen</Button>
                <Button className="w-full sm:w-auto" disabled={!assigneeUserId} variant="outline" onClick={() => void onTriageAndStartInterview(selectedRequest, { assigneeUserId, dueDate: dueDate || undefined, priority: priority || selectedRequest.priority, adminNote })}>Zuweisen & in Sopho Interviewer starten</Button>
                <Button className="w-full border-rose-200 text-rose-700 sm:w-auto" variant="outline" onClick={() => void onReject(selectedRequest.id, { comment: adminNote || undefined })}>Request ablehnen</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewView({
  requests,
  loading,
  onSelectRequest,
  onReview,
  onOpenSteward,
}: {
  requests: ClarifyRequestSummary[];
  loading: boolean;
  onSelectRequest: (requestId: string) => void;
  onReview: (requestId: string, input: ReviewClarifyRequestInput) => Promise<void>;
  onOpenSteward: () => void;
}) {
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const selectedRequest = requests.find((request) => request.id === selectedRequestId) ?? null;

  return (
    <div className="grid min-h-0 gap-6 py-4 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Card>
        <CardContent className="flex h-full flex-col gap-3 p-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-violet-600" />
            <h2 className="text-sm font-semibold">Review-Queue</h2>
            <Badge variant="outline" className="ml-auto">{requests.length} zur Prüfung</Badge>
          </div>
          <div className="max-h-[40vh] min-h-0 flex-1 space-y-2 overflow-auto lg:max-h-none">
            {requests.length === 0 && !loading ? <EmptyState text="Keine Antworten zur Prüfung" compact /> : null}
            {requests.map((request) => (
              <button key={request.id} type="button" onClick={() => setSelectedRequestId(request.id)} className={`w-full rounded-lg border p-3 text-left transition-colors ${selectedRequestId === request.id ? 'border-primary bg-secondary/70' : 'border-border/70 hover:bg-secondary/40'}`}>
                <div className="mb-1 flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{request.requestNumber}</span>
                  <ClarifyDetailStatusBadge status={request.detailStatus} />
                  <ClarifyPriorityBadge priority={request.priority} />
                </div>
                <p className="text-sm font-semibold">{request.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{request.assignee?.displayName ?? 'Kein Experte zugewiesen'}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex h-full flex-col gap-4 p-5">
          {!selectedRequest ? <EmptyState text="Antwort aus der Queue auswählen" /> : (
            <>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{selectedRequest.requestNumber}</span>
                  <ClarifyMainStatusBadge status={selectedRequest.mainStatus} />
                  <ClarifyDetailStatusBadge status={selectedRequest.detailStatus} />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">{selectedRequest.title}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{selectedRequest.question}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => onSelectRequest(selectedRequest.id)}>Details</Button>
                </div>
              </div>

              <Card>
                <CardContent className="space-y-2 p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Review-Kommentar</h3>
                  <Textarea value={comment} onChange={(event) => setComment(event.target.value)} className="min-h-[120px]" placeholder="Entscheidung oder Nacharbeitswunsch dokumentieren" />
                </CardContent>
              </Card>

              {selectedRequest.interviewSessionId ? (
                <div className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/70 p-4">
                  <p className="text-sm text-sky-950">Dieser Request ist mit einem Interview-Report verknüpft. Die fachliche Freigabe, Nacharbeit oder Ablehnung erfolgt in Sopho Steward über den Report.</p>
                  <Button className="w-full sm:w-auto" variant="outline" onClick={onOpenSteward}>Sopho Steward öffnen</Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <Button className="w-full gap-2 sm:w-auto bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => void onReview(selectedRequest.id, { action: 'approve', comment })}><CheckCircle2 className="h-4 w-4" />Freigeben</Button>
                  <Button className="w-full sm:w-auto" variant="outline" onClick={() => void onReview(selectedRequest.id, { action: 'revision', comment })}>Nacharbeit anfordern</Button>
                  <Button className="w-full sm:w-auto border-rose-200 text-rose-700" variant="outline" onClick={() => void onReview(selectedRequest.id, { action: 'reject', comment })}>Ablehnen</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RequestDetailView({
  request,
  onBack,
  onStartInterview,
  onOpenLinkedDocument,
  onPreviewLinkedDocument,
}: {
  request: ClarifyRequestDetail;
  onBack: () => void;
  onStartInterview: (request: ClarifyRequestSummary) => Promise<void>;
  onOpenLinkedDocument: (documentId: string) => void;
  onPreviewLinkedDocument: (documentId: string, fileName: string) => void;
}) {
  const steps: Array<{ label: string; active: boolean }> = [
    { label: 'Request', active: request.mainStatus === 'request' || request.mainStatus === 'in_progress' || request.mainStatus === 'in_review' || request.mainStatus === 'solved' },
    { label: 'In Progress', active: request.mainStatus === 'in_progress' || request.mainStatus === 'in_review' || request.mainStatus === 'solved' },
    { label: 'In Review', active: request.mainStatus === 'in_review' || request.mainStatus === 'solved' },
    { label: 'Solved', active: request.mainStatus === 'solved' },
  ];

  const canWork = request.detailStatus === 'assigned' || request.detailStatus === 'accepted' || request.detailStatus === 'interview_in_progress' || request.detailStatus === 'awaiting_input' || request.detailStatus === 'revision_requested';

  return (
    <div className="space-y-6 overflow-auto py-2">
      <div>
        <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />Zurück zur Übersicht
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground">{request.requestNumber}</span>
          <ClarifyMainStatusBadge status={request.mainStatus} />
          <ClarifyDetailStatusBadge status={request.detailStatus} />
          <ClarifyPriorityBadge priority={request.priority} />
        </div>
        <h1 className="mt-2 text-2xl font-bold">{request.title}</h1>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {steps.map((step) => (
          <div key={step.label} className="space-y-2">
            <div className={`h-2 rounded-full ${step.active ? 'bg-primary' : 'bg-muted'}`} />
            <p className={`text-xs font-medium ${step.active ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_340px]">
        <div className="space-y-5">
          <Card>
            <CardContent className="space-y-2 p-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Fachliche Frage</h3>
              <p className="text-sm leading-relaxed">{request.question}</p>
            </CardContent>
          </Card>

          {request.businessContext ? (
            <BusinessContextCard businessContext={request.businessContext} />
          ) : null}

          {request.adminNote ? (
            <Card className="border-sky-200 bg-sky-50/60">
              <CardContent className="space-y-2 p-5">
                <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Admin-Notiz</h3>
                <p className="text-sm">{request.adminNote}</p>
              </CardContent>
            </Card>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {request.tags.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}
          </div>

          <Card>
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Verlauf</h3>
                {canWork ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    <Button size="sm" className="w-full gap-2 sm:w-auto" onClick={() => void onStartInterview(request)}><Mic className="h-4 w-4" />Interviewer öffnen</Button>
                  </div>
                ) : null}
              </div>
              <div className="space-y-4">
                {request.timeline.map((entry, index) => (
                  <div key={entry.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="mt-1 h-2 w-2 rounded-full bg-primary" />
                      {index < request.timeline.length - 1 ? <div className="h-full w-px bg-border" /> : null}
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium">{entry.description}</p>
                      <p className="text-xs text-muted-foreground">{entry.actorDisplayName} · {new Date(entry.createdAt).toLocaleString('de-DE')}</p>
                      {entry.note ? <p className="mt-1 text-sm text-muted-foreground">{entry.note}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Beteiligte</h3>
              <PersonRow label="Requester" person={request.requester} icon={<User className="h-4 w-4" />} />
              {request.assignee ? <PersonRow label="Experte" person={request.assignee} icon={<Mic className="h-4 w-4" />} /> : null}
              {request.reviewer ? <PersonRow label="Reviewer" person={request.reviewer} icon={<ClipboardCheck className="h-4 w-4" />} /> : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Termine</h3>
              <MetaRow label="Erstellt" value={new Date(request.createdAt).toLocaleDateString('de-DE')} />
              <MetaRow label="Letztes Update" value={new Date(request.updatedAt).toLocaleDateString('de-DE')} />
              {request.dueDate ? <MetaRow label="Fällig" value={new Date(request.dueDate).toLocaleDateString('de-DE')} /> : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Verknüpfungen</h3>
              {request.interviewTaskNumber ? <div className="rounded-lg bg-secondary/60 p-3 text-sm"><p className="font-medium">Sopho Interviewer</p><p className="text-xs text-muted-foreground">Task {request.interviewTaskNumber}</p></div> : null}
              {request.linkedDocuments.length > 0 ? (
                <div className="space-y-2 rounded-lg bg-secondary/60 p-3 text-sm">
                  <p className="font-medium">Sopho Data Viewer</p>
                  <div className="space-y-2">
                    {request.linkedDocuments.map((document) => (
                      <div key={document.id} className="rounded-md border border-border/60 bg-background/80 p-3">
                        <p className="truncate font-medium">{document.fileName}</p>
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                          <Button size="sm" className="w-full sm:w-auto" variant="outline" onClick={() => onOpenLinkedDocument(document.id)}>
                            Im Data Viewer öffnen
                          </Button>
                          <Button size="sm" className="w-full sm:w-auto" variant="ghost" onClick={() => onPreviewLinkedDocument(document.id, document.fileName)}>
                            Vorschau
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="rounded-lg bg-secondary/60 p-3 text-sm"><p className="font-medium">Sopho Steward</p><p className="text-xs text-muted-foreground">Review-Entscheidungen werden auditierbar protokolliert.</p></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function RequestList({
  requests,
  loading,
  emptyText,
  onSelectRequest,
}: {
  requests: ClarifyRequestSummary[];
  loading: boolean;
  emptyText: string;
  onSelectRequest: (requestId: string) => void;
}) {
  return (
    <div className="space-y-3">
      {loading ? <LoaderRow /> : null}
      {requests.length === 0 && !loading ? <EmptyState text={emptyText} /> : null}
      {requests.map((request) => (
        <Card key={request.id} className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => onSelectRequest(request.id)}>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{request.requestNumber}</span>
              <ClarifyMainStatusBadge status={request.mainStatus} />
              <ClarifyDetailStatusBadge status={request.detailStatus} />
              <ClarifyPriorityBadge priority={request.priority} />
            </div>
            <div>
              <h3 className="text-sm font-semibold">{request.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{request.question}</p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span>{request.requester.displayName}</span>
              {request.assignee ? <span className="flex items-center gap-1"><User className="h-3 w-3" />{request.assignee.displayName}</span> : null}
              {request.dueDate ? <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(request.dueDate).toLocaleDateString('de-DE')}</span> : null}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PersonRow({ label, person, icon }: { label: string; person: ClarifyRequestDetail['requester']; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 p-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-muted-foreground">{icon}</div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{person.displayName}</p>
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function EmptyState({ text, compact = false }: { text: string; compact?: boolean }) {
  return <div className={`flex items-center justify-center rounded-lg border border-dashed border-border/70 text-sm text-muted-foreground ${compact ? 'p-6' : 'p-10'}`}>{text}</div>;
}

function LoaderRow() {
  return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Lade Daten…</div>;
}